import { selMessage, urls } from "@selector/shared";
import { type FC, type IframeHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Center,
  DialogComponent,
  formatDateTimeFromISO,
  LoadingSpinner,
  MarkdownPreview,
  Modal,
  TagIntent,
  TooltipIntent,
  TriggerButton,
} from "widgets";
import { type ControlledDialog, useOverlay, useVisuallyLoading } from "widgets/hooks";

import { useError } from "@/app-utils/graphql/useError.ts";
import { capitalizeFirstLetter } from "@/app-utils/string.ts";
import type { ProjectFile } from "@/graphql.ts";
import { FileDownloadButton } from "@/modules/files/components/FileDownloadButton";
import { FilePreviewTextField } from "@/modules/files/components/FilePreviewTextField";
import { HeaderIcon } from "@/modules/files/components/HeaderIcon";
import { PDFPreview, type PdfPreviewActionsApi } from "@/modules/files/components/PDFPreview";
import { usePreviewDialogBasicsLazyQuery } from "@/modules/files/graphql/files-queries.gql";
import style from "@/modules/files/styles/FilePreviewDialog.module.scss";
import { getFilePreview } from "@/modules/files/utils/fetch-preview.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { HTML_FILE_PREVIEW } from "@/settings/settings.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

interface FilePreviewDialogProps extends ControlledDialog {
  file: Pick<ProjectFile, "id" | "fileName" | "fileBrowseType" | "lastModified">;
  isOpen: boolean;
}

export const FilePreviewControlledDialog: FC<FilePreviewDialogProps> = (props) => {
  const pdfPreviewActionsApiRef = useRef<PdfPreviewActionsApi>(null);
  const { state, overlayProps } = useOverlay({
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        props.onCancel?.();
      }
    },
  });

  const [previewType, setPreviewType] = useState<{ contentType: string; charset: string }>();
  const [previewContent, setPreviewContent] = useState<string>();
  const htmlFilePreviewMode = useSelector<IAppState, HTML_FILE_PREVIEW | null>(
    (state) => state.config.configuration.application?.htmlFilePreview as HTML_FILE_PREVIEW | null,
  );
  // define the iframe security for html file preview based on the configured setting
  const htmlPreviewSecurityOptions: IframeHTMLAttributes<HTMLIFrameElement> = {};
  if (htmlFilePreviewMode === HTML_FILE_PREVIEW.STRICT) {
    htmlPreviewSecurityOptions.sandbox = "allow-scripts";
  }

  // load the data
  const [loadDialogBasics, { data, loading: isLoadingDialog, error }] = usePreviewDialogBasicsLazyQuery({
    variables: { fileId: props.file.id },
  });
  void useError(error);

  const [isLoadingFile, setLoadingFile] = useState<boolean>(true);
  const isLoading = useVisuallyLoading(isLoadingDialog || isLoadingFile, 300);
  const file = data?.projectFileById;

  const [fetchError, setFetchError] = useState<string>();

  const handlePreviewResponse = useCallback((res: Awaited<ReturnType<typeof getFilePreview>>) => {
    if (res.error) {
      setFetchError(res.error);
      setLoadingFile(false);
      return;
    }

    if (res.type == null || res.content == null) {
      setFetchError("An error occurred");
      setLoadingFile(false);
      return;
    }

    const splitTypeAndCharset = res.type.split(";");
    const contentType = splitTypeAndCharset[0].trim();
    const charset = splitTypeAndCharset[1]?.trim();
    setPreviewType({ contentType, charset });

    if (contentType === "text/plain" || contentType === "text/markdown") {
      setPreviewContent(res.content as string);
      setLoadingFile(false);
    } else {
      const reader = new FileReader();
      reader.readAsArrayBuffer(res.content as Blob);
      reader.onload = (e) => {
        const buffer = e.target?.result;
        if (buffer) {
          const previewFile = new File([buffer], "preview", {
            type: contentType,
          });
          setPreviewContent(URL.createObjectURL(previewFile));
        }
        setLoadingFile(false);
      };
    }
  }, []);

  useEffect(() => {
    if (props.isOpen) {
      state.open();
    } else {
      state.close();
    }
  }, [props.isOpen, state]);

  useEffect(() => {
    if (state.isOpen) {
      void loadDialogBasics();
      void getFilePreview(props.file.id).then(handlePreviewResponse);
    }
  }, [handlePreviewResponse, loadDialogBasics, props.file.id, state.isOpen]);

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        title={props.file.fileName ?? ""}
        subtitle={
          props.file.lastModified
            ? `Modified on ${formatDateTimeFromISO(props.file.lastModified, { hideTime: true })}`
            : undefined
        }
        icon={file ? <HeaderIcon file={props.file} /> : undefined}
        closeFn={() => {
          props.onCancel?.();
        }}
        fullscreen
        actions={[
          previewType?.contentType === "application/pdf" && (
            <>
              <TriggerButton
                showLabel={true}
                icon="add_circle"
                label="Zoom In"
                onPress={() => pdfPreviewActionsApiRef.current?.zoomIn()}
              />
              <TriggerButton
                showLabel={true}
                icon="do_not_disturb_on"
                label="Zoom Out"
                onPress={() => pdfPreviewActionsApiRef.current?.zoomOut()}
              />
            </>
          ),
          <FileDownloadButton file={file} key={`preview-dialog-download-button-${props.file.id}`} />,
        ]}
        contentStyle={{ padding: 0, display: "flex", width: "100%", height: "100%", overflow: "hidden" }}
        tag={
          previewType?.contentType === "text/html" && htmlFilePreviewMode === HTML_FILE_PREVIEW.STRICT
            ? {
                label: htmlFilePreviewMode,
                intentColor: TagIntent.Warning,
                tooltip: {
                  intent: TooltipIntent.Warning,
                  title: selMessage({
                    id: "config.application.htmlPreview.strict.title",
                    values: { mode: capitalizeFirstLetter(htmlFilePreviewMode) },
                  }),
                  children: selMessage({ id: "config.application.htmlPreview.strict.message" }),
                },
              }
            : undefined
        }
      >
        {/* do not include !preview within isVisuallyLoading because it is too fast and the result will never be true*/}
        {!fetchError && (isLoading || previewContent == undefined || !previewType) ? (
          <LoadingSpinner inCenter />
        ) : fetchError ? (
          <ContentPlaceholder>{fetchError}</ContentPlaceholder>
        ) : (
          previewContent &&
          // most specific types first
          (previewType?.contentType === "text/html" ? (
            <div className={style.iframeContainer}>
              <iframe
                key={`html-preview`}
                title={file?.fileName ?? ""}
                src={previewContent}
                className={style.iFrameElement}
                itemType={previewType.contentType}
                draggable={false}
                // When the embedded document has the same origin as the embedding page,
                // it is strongly discouraged to use both allow-scripts and allow-same-origin,
                // as that lets the embedded document remove the sandbox attribute —
                // making it no more secure than not using the sandbox attribute at all.
                // @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe
                //! With `allow-same-origin` you have access to the localStorage and so any users token
                //! (If you ask them to open the preview with a malicious script)
                //! => Same for opening the file in another tab. Disallow that! Then all security are off.
                {...htmlPreviewSecurityOptions}
              />
            </div>
          ) : previewType?.contentType === "text/plain" ? (
            <FilePreviewTextField key={`text-preview`} value={previewContent} style={{ borderWidth: "1px 0 0" }} />
          ) : previewType?.contentType === "text/markdown" ? (
            <Card noBorder scrollable>
              <MarkdownPreview markdownText={previewContent} baseUrl={urls.basePath} />
            </Card>
          ) : previewType?.contentType === "application/pdf" ? (
            <PDFPreview pdfUrl={previewContent} ref={pdfPreviewActionsApiRef} />
          ) : // images have specific types, but all of them are rendered the same way
          previewType?.contentType.startsWith("image/") ? (
            <Center className={style.imagePreviewContainer}>
              <img
                key={`image-preview`}
                src={previewContent}
                className={style.imagePreview}
                alt={`image preview`}
                draggable={false}
              />
            </Center>
          ) : (
            // if we cannot map the type to a specific renderer
            <ContentPlaceholder>
              {selMessage({ id: "file.preview.unsupported", values: { fileType: previewType?.contentType } })}
            </ContentPlaceholder>
          ))
        )}
      </DialogComponent>
    </Modal>
  );
};
